import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { PhoneNumberManagement } from "@/components/PhoneNumberManagement";
import { toast } from "sonner";

export default function UserSettings() {
  const { user, loading: authLoading } = useAuth();
  const [, navigate] = useLocation();

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/");
    }
  }, [authLoading, user, navigate]);

  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch current preferences
  const { data: preferences, isLoading: isLoadingPreferences } = trpc.admin.notificationPreferences.getMyPreferences.useQuery(
    undefined,
    {
      enabled: !!user,
    }
  );

  // Fetch phone verification status
  const { data: phoneStatus, isLoading: isLoadingPhoneStatus } = trpc.admin.phoneManagement.getPhoneStatus.useQuery(
    undefined,
    {
      enabled: !!user,
    }
  );

  // Local state for form
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [lineNotifications, setLineNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [notificationFrequency, setNotificationFrequency] = useState<"immediate" | "daily" | "weekly">("immediate");

  // Update local state when preferences load
  useEffect(() => {
    if (preferences) {
      setEmailNotifications(preferences.emailNotifications);
      setLineNotifications(preferences.lineNotifications);
      setSmsNotifications(preferences.smsNotifications);
      setNotificationFrequency(preferences.notificationFrequency);
    }
  }, [preferences]);

  // Update preferences mutation
  const updateMutation = trpc.admin.notificationPreferences.updatePreferences.useMutation({
    onSuccess: () => {
      setSaveStatus("success");
      setErrorMessage("");
      setTimeout(() => setSaveStatus("idle"), 3000);
    },
    onError: (error: any) => {
      setSaveStatus("error");
      setErrorMessage(error.message || "Failed to save preferences");
      setTimeout(() => setSaveStatus("idle"), 5000);
    },
  });

  const handleSave = async () => {
    setSaveStatus("saving");
    await updateMutation.mutateAsync({
      emailNotifications,
      lineNotifications,
      smsNotifications,
      notificationFrequency,
    });
  };

  if (authLoading || isLoadingPreferences) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-black" />
          <p className="text-black/60">Loading your settings...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const hasAtLeastOneChannelEnabled = emailNotifications || lineNotifications || smsNotifications;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b-4 border-black bg-white">
        <div className="container py-8">
          <h1 className="font-heading font-bold text-4xl tracking-tight">Settings</h1>
          <p className="text-black/60 mt-2">Manage your notification preferences and account settings</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-12">
        <div className="max-w-2xl">
          {/* Status Messages */}
          {saveStatus === "success" && (
            <div className="mb-6 p-4 border-2 border-black bg-white flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-black flex-shrink-0" />
              <p className="text-black font-medium">Preferences saved successfully</p>
            </div>
          )}

          {saveStatus === "error" && (
            <div className="mb-6 p-4 border-2 border-black bg-white flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-black flex-shrink-0" />
              <p className="text-black font-medium">{errorMessage}</p>
            </div>
          )}

          {/* Notification Channels Card */}
          <Card className="border-2 border-black rounded-none shadow-none mb-6">
            <CardHeader className="border-b-2 border-black">
              <CardTitle className="font-heading text-2xl">Notification Channels</CardTitle>
              <CardDescription className="text-black/60">
                Choose how you want to receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-center justify-between p-4 border-2 border-black">
                  <div className="flex-1">
                    <Label className="font-heading font-semibold text-base cursor-pointer">Email</Label>
                    <p className="text-sm text-black/60 mt-1">Receive notifications via email</p>
                  </div>
                  <Switch
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                    className="ml-4"
                    aria-label="Enable email notifications"
                  />
                </div>

                {/* LINE */}
                <div className="flex items-center justify-between p-4 border-2 border-black">
                  <div className="flex-1">
                    <Label className="font-heading font-semibold text-base cursor-pointer">LINE</Label>
                    <p className="text-sm text-black/60 mt-1">Receive notifications via LINE messaging</p>
                  </div>
                  <Switch
                    checked={lineNotifications}
                    onCheckedChange={setLineNotifications}
                    className="ml-4"
                    aria-label="Enable LINE notifications"
                  />
                </div>

                {/* SMS */}
                <div className={`flex items-center justify-between p-4 border-2 border-black ${!phoneStatus?.isVerified ? 'opacity-60' : ''}`}>
                  <div className="flex-1">
                    <Label className="font-heading font-semibold text-base cursor-pointer">SMS</Label>
                    <p className="text-sm text-black/60 mt-1">
                      {phoneStatus?.isVerified
                        ? "Receive notifications via text message"
                        : "Add and verify a phone number to enable SMS notifications"}
                    </p>
                  </div>
                  <Switch
                    checked={smsNotifications}
                    onCheckedChange={setSmsNotifications}
                    disabled={!phoneStatus?.isVerified}
                    className="ml-4"
                    aria-label="Enable SMS notifications"
                  />
                </div>

                {/* Warning if no channels enabled */}
                {!hasAtLeastOneChannelEnabled && (
                  <div className="p-4 border-2 border-black bg-black/5">
                    <p className="text-sm text-black font-medium">
                      ⚠️ At least one notification channel must be enabled
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notification Frequency Card */}
          <Card className="border-2 border-black rounded-none shadow-none mb-6">
            <CardHeader className="border-b-2 border-black">
              <CardTitle className="font-heading text-2xl">Notification Frequency</CardTitle>
              <CardDescription className="text-black/60">
                How often do you want to receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="p-4 border-2 border-black">
                <Label htmlFor="frequency" className="font-heading font-semibold text-base block mb-3">
                  Delivery Frequency
                </Label>
                <Select value={notificationFrequency} onValueChange={(value: any) => setNotificationFrequency(value)}>
                  <SelectTrigger id="frequency" className="border-2 border-black rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediate - Get notified right away</SelectItem>
                    <SelectItem value="daily">Daily - Receive a daily digest</SelectItem>
                    <SelectItem value="weekly">Weekly - Receive a weekly digest</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-black/60 mt-2">
                  {notificationFrequency === "immediate" &&
                    "You'll receive notifications as soon as events occur."}
                  {notificationFrequency === "daily" && "You'll receive a daily summary at 9:00 AM."}
                  {notificationFrequency === "weekly" && "You'll receive a weekly summary every Monday at 9:00 AM."}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Phone Number Management */}
          <PhoneNumberManagement />

          {/* Save Button */}
          <div className="flex gap-4">
            <Button
              onClick={handleSave}
              disabled={!hasAtLeastOneChannelEnabled || saveStatus === "saving"}
              className="bg-black text-white border-2 border-black hover:bg-white hover:text-black font-heading font-semibold px-8 py-6 text-base transition-colors"
            >
              {saveStatus === "saving" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Preferences"
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/account")}
              className="border-2 border-black text-black hover:bg-black/5 font-heading font-semibold px-8 py-6 text-base"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
